import {
    ADDITIONAL_PAGE_OPTIONS,
    MAP_TARIFF_ID_TO_SALESCHANNEL,
    RedplusTariff,
    RedTariff,
    SAILS_PARAM_SALESCHANNEL,
    SAILS_PARAM_SUB_ID,
    SAILS_REDPLUS_STORAGE,
    SalesChannel,
    SALESCHANNEL_CONSUMER,
    SALESCHANNEL_REDPLUS,
    URL_PARAM_GIGAKOMBI,
    URL_PARAM_SUBGROUP_ID,
    YoungTariff,
    FAMILYCARD_S_VIRTUAL_ID,
    SAILS_BNT_STORAGE,
    MAP_SALESCHANNEL_TO_VIRTUAL_ID,
} from '@vfde-sails/constants';
import {
    getSessionStorageItemJson,
    RedPlusStorage,
} from '@vfde-sails/storage';
import { getQueryParam } from '@vfde-sails/utils';
import { SailsDeviceOverviewStorage } from '../../../interfaces/storage';

/**
 * Get the subscription ID from URL or session storage and validate against saleschannel
 */
export const getSubscriptionId = (
    salesChannel: SalesChannel = SALESCHANNEL_CONSUMER,
): RedTariff | YoungTariff | RedplusTariff | undefined => {
    // Default subscription id
    let subscriptionId = getSubscriptionFromUrl() ?? getSubscriptionFromStorage();

    // Redplus subscription id
    if (salesChannel === SALESCHANNEL_REDPLUS) {
        subscriptionId = getSubscriptionFromUrl() ?? getSubscriptionFromRedplusStorage() ?? getSubscriptionFromStorage();
    }

    if (subscriptionId && validateSubscriptionId(subscriptionId, salesChannel)) {
        return subscriptionId;
    }

    // For redplus: if no valid subscriptionId, or subscriptionId is not redplus, we return Family Card S as default
    if (salesChannel === SALESCHANNEL_REDPLUS && (!subscriptionId || !validateSubscriptionId(subscriptionId, salesChannel))) {
        return window[ADDITIONAL_PAGE_OPTIONS].defaultRedplusId || FAMILYCARD_S_VIRTUAL_ID;
    }
};

/**
 * Get gigakombi from URL
 */
export const getGigakombiFromUrl = (): true | undefined => {
    const gigakombiParam = getQueryParam(URL_PARAM_GIGAKOMBI) as string | null;

    return (gigakombiParam === '1' || gigakombiParam === 'true' || undefined);
};

/**
 * Get subscription ID from URL
 */
export const getSubscriptionFromUrl = (): RedTariff | YoungTariff | RedplusTariff | undefined => {
    const urlParamSubGroupId = getQueryParam(URL_PARAM_SUBGROUP_ID) as string | null;

    if (urlParamSubGroupId && urlParamSubGroupId in MAP_TARIFF_ID_TO_SALESCHANNEL) {
        return urlParamSubGroupId as RedTariff | YoungTariff | RedplusTariff;
    }
};

/**
 * Get subscription ID from storage
 */
export const getSubscriptionFromStorage = (): RedTariff | YoungTariff | RedplusTariff | undefined => {
    const storage = getSessionStorageItemJson<SailsDeviceOverviewStorage>(SAILS_BNT_STORAGE);

    return storage?.[SAILS_PARAM_SUB_ID]?.[storage[SAILS_PARAM_SALESCHANNEL]];
};

/**
 * Get Redplus subscription ID from storage
 */
export const getSubscriptionFromRedplusStorage = (): RedplusTariff | undefined => {
    const storage = getSessionStorageItemJson<RedPlusStorage>(SAILS_REDPLUS_STORAGE);

    return storage?.[SAILS_PARAM_SUB_ID]?.toString() as RedplusTariff;
};

/**
 * Get the saleschannel from the additional page options OR storage
 */
export const getSalesChannel = (): SalesChannel | undefined => {
    const storage = getSessionStorageItemJson<SailsDeviceOverviewStorage>(SAILS_BNT_STORAGE);

    return window[ADDITIONAL_PAGE_OPTIONS].salesChannel || storage?.[SAILS_PARAM_SALESCHANNEL];
};

/**
 * Validate subscription ID
 */
export const validateSubscriptionId = (
    id: RedTariff | YoungTariff | RedplusTariff,
    salesChannel: SalesChannel,
): boolean => MAP_SALESCHANNEL_TO_VIRTUAL_ID[salesChannel].includes(id);
